/**
 * Regrid parcel lookup — address search, then parcel fetch.
 *
 * Two public endpoints, chained:
 *
 *   1. /search.json?query=<address>&autocomplete=1&context=false&strict=false
 *      -> [{ category: "parcel", path: "/us/or/grant/john-day/11852",
 *            parcelnumb: "13S31E3200700", headline: "26637 Luce Creek Rd",
 *            context: "County, OR", type: "Parcel", score: 87.1 }]
 *
 *   2. <path>.json?source_ids=&current_region_path=
 *      -> { id, headline, fields: {...}, geometry, centroid, spec: { geoid } }
 *
 * The `path` from step 1 is exactly what step 2 needs, so an address resolves to a polygon in
 * two calls.
 *
 * ⚠️ A 429 is NOT a failure here. The body still carries the full `geometry`, `centroid` and
 * `formatted_addresses`; only `fields` comes back empty. Grid membership, county, acreage and
 * the election test are all computed from the polygon, so a rate-limited reply serves this
 * app's purpose completely and is passed through as a success.
 *
 * ⚠️ Never retry a 429. Re-requesting the same URL is what gets a caller flagged, and there is
 * nothing to gain: the only fields a clean response would add are ones this app does not use.
 * Rate limiting is mitigated by SPACING instead — a module-level gate serialises requests and
 * holds each one MIN_GAP_MS behind the previous, since the search and the parcel fetch are one
 * user action but two calls, and firing them back to back is what earns the 429.
 *
 * CORS: requests do work from the browser in practice. `REACT_APP_REGRID_PROXY` routes through
 * a same-origin proxy when set, and a genuinely blocked request falls back to paste-the-JSON.
 */

const PROXY = process.env.REACT_APP_REGRID_PROXY || "";
const ORIGIN = "https://app.regrid.com";

// With a proxy, paths are rewritten onto it; without one, they hit Regrid directly.
const url = (path) => (PROXY ? `${PROXY.replace(/\/$/, "")}${path}` : `${ORIGIN}${path}`);

export class RegridError extends Error {
  constructor(message, kind) {
    super(message);
    this.name = "RegridError";
    this.kind = kind; // "cors" | "not_found" | "bad_response"
  }
}

const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

// Minimum spacing between requests. The search and the parcel fetch fire from one click, and
// issuing them back to back is what earns the 429 — this gate alone removes most of them.
const MIN_GAP_MS = 900;

let lastAt = 0;
let chain = Promise.resolve();

/** Serialises requests and holds each one MIN_GAP_MS behind the previous. */
const gate = (fn) => {
  const run = chain.then(async () => {
    const wait = MIN_GAP_MS - (Date.now() - lastAt);
    if (wait > 0) await sleep(wait);
    try {
      return await fn();
    } finally {
      lastAt = Date.now();
    }
  });
  // Keep the chain alive even when a call rejects, or one failure stalls every later request.
  chain = run.catch(() => {});
  return run;
};

/** One request per call. A 429 body is returned as-is — see the header note. */
const getJson = (path) =>
  gate(async () => {
    let res;
    try {
      res = await fetch(url(path), { headers: { Accept: "application/json" } });
    } catch {
      // A CORS rejection and a genuine network failure are indistinguishable from script —
      // the browser deliberately withholds the difference. Report the actionable one.
      throw new RegridError(
        PROXY
          ? "Could not reach the Regrid proxy."
          : "Could not reach Regrid from the browser. Paste the parcel JSON instead, or set REACT_APP_REGRID_PROXY.",
        "cors",
      );
    }
    if (res.status === 404) throw new RegridError("No such parcel.", "not_found");
    if (!res.ok && res.status !== 429)
      throw new RegridError(`Regrid returned ${res.status}.`, "bad_response");

    // Same trap as the PRF dataset behind CloudFront: an HTML error page can arrive with a
    // 200, and `.json()` on the shell fails with an opaque parse error.
    const ct = res.headers.get("content-type") || "";
    if (!ct.includes("json"))
      throw new RegridError("Regrid returned a non-JSON response.", "bad_response");

    return res.json();
  });

/** Address/APN autocomplete. Returns only parcel hits — the endpoint also returns places. */
export const searchParcels = async (query) => {
  const q = (query || "").trim();
  if (!q) return [];
  const params = new URLSearchParams({
    query: q,
    autocomplete: "1",
    context: "false",
    strict: "false",
  });
  const j = await getJson(`/search.json?${params}`);
  return (Array.isArray(j) ? j : [])
    .filter((r) => r && r.path && (r.category === "parcel" || r.type === "Parcel"))
    .map((r) => ({
      path: r.path,
      parcelNumber: r.parcelnumb || null,
      headline: r.headline || r.path,
      context: r.context || null,
      score: r.score ?? null,
    }));
};

/** Full parcel record for a `path` from searchParcels (or pasted from the address bar). */
export const fetchParcel = async (path) => {
  const clean = String(path || "")
    .replace(/^https?:\/\/[^/]+/, "")
    .replace(/\.json.*$/, "")
    .replace(/\?.*$/, "")
    .replace(/\/$/, "");
  if (!clean.startsWith("/")) throw new RegridError("Not a Regrid parcel path.", "not_found");
  return getJson(`${clean}.json?source_ids=&current_region_path=`);
};

/** True when the input looks like a Regrid parcel URL or path rather than a free-text address. */
export const looksLikePath = (s) =>
  /^(https?:\/\/(app\.)?regrid\.com)?\/us\/[a-z]{2}\//i.test((s || "").trim());
